import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';

import { SERIES_COLORS_AREA } from './constants';
import { CustomDateTick } from './CustomDateTick';
import { CHART_GRID_COLOR, CHART_TICK_COLOR } from '../consts';
import { resolveMetricLabel, formatCompact, chartLegendFormatter } from '../../../../helpers';
import { ChartTooltip } from '../TooltipComponent';

interface AreaSeriesChartProps {
  chartData: Array<Record<string, unknown> & { date: string }>;
  metrics: DynamicModuleMetric[];
  chartHeight?: number;
}

export const AreaSeriesChart = ({ chartData, metrics, chartHeight }: AreaSeriesChartProps) => {
  const { t } = useTranslation('reports');

  return (
    <ResponsiveContainer
      width="100%"
      height={chartHeight || '100%'}
    >
      <AreaChart
        data={chartData}
        margin={{ top: 25, right: 0, left: -20, bottom: 0 }}
      >
        <defs>
          {metrics.map((metric, idx) => (
            <linearGradient
              key={`grad-${metric.name}`}
              id={`grad-${metric.name}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor={SERIES_COLORS_AREA[idx % SERIES_COLORS_AREA.length]}
                stopOpacity={0.2}
              />
              <stop
                offset="95%"
                stopColor={SERIES_COLORS_AREA[idx % SERIES_COLORS_AREA.length]}
                stopOpacity={0.02}
              />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid
          strokeDasharray="0"
          stroke={CHART_GRID_COLOR}
          vertical={false}
        />
        <XAxis
          dataKey="date"
          tick={<CustomDateTick chartData={chartData} />}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
          tickMargin={5}
        />
        <YAxis
          tick={{ fontSize: 12, fill: CHART_TICK_COLOR, textAnchor: 'end' }}
          tickCount={10}
          tickFormatter={formatCompact}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<ChartTooltip />} />
        <Legend
          align="left"
          iconType="circle"
          iconSize={12}
          wrapperStyle={{
            fontSize: '12px',
            marginBottom: '3px',
            marginTop: '5px',
            marginLeft: '20px',
          }}
          formatter={chartLegendFormatter}
        />
        {metrics.map((metric, idx) => (
          <Area
            key={metric.name}
            type="monotone"
            dataKey={metric.name}
            name={resolveMetricLabel(metric.name, t)}
            stroke={SERIES_COLORS_AREA[idx % SERIES_COLORS_AREA.length]}
            strokeWidth={2}
            fill={`url(#grad-${metric.name})`}
            dot={false}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};
